'use client';

import { useEffect, useRef } from 'react';
import PageSectionType from '@alltrails/amplitude/enums/PageSectionType';
import PageType from '@alltrails/amplitude/enums/PageType';
import logWebSectionViewed from '@alltrails/amplitude/events/logWebSectionViewed';
import {
  LOCATION_SECTION_ID_GUIDES_CAROUSEL,
  LOCATION_SECTION_ID_LOCATION_FAQ,
  LOCATION_SECTION_ID_REVIEWS,
  LOCATION_SECTION_ID_SEO_LINKS,
  LOCATION_SECTION_ID_TOP_AREAS,
  LOCATION_SECTION_ID_TOP_MODULES,
  LOCATION_SECTION_ID_TOP_TRAILS,
  LOCATION_SECTION_ID_VISITOR_INFO
} from '@/constants/locationSectionIds';

const SECTIONS: { id: string; sectionType: PageSectionType; threshold?: number }[] = [
  { id: LOCATION_SECTION_ID_GUIDES_CAROUSEL, sectionType: 'Guides', threshold: 1 },
  { id: LOCATION_SECTION_ID_LOCATION_FAQ, sectionType: 'FAQ', threshold: 0.4 },
  { id: LOCATION_SECTION_ID_REVIEWS, sectionType: 'Trail Reviews' },
  { id: LOCATION_SECTION_ID_SEO_LINKS, sectionType: 'Link Block', threshold: 0.4 },
  { id: LOCATION_SECTION_ID_TOP_AREAS, sectionType: 'Highlights', threshold: 0.1 },
  { id: LOCATION_SECTION_ID_TOP_MODULES, sectionType: 'Top Modules', threshold: 0.5 },
  { id: LOCATION_SECTION_ID_TOP_TRAILS, sectionType: 'Highlights', threshold: 0.1 },
  { id: LOCATION_SECTION_ID_VISITOR_INFO, sectionType: 'Visitor Info', threshold: 0.6 }
];

const SECTIONS_BY_ID = Object.fromEntries(SECTIONS.map(s => [s.id, s]));

const THRESHOLDS = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];

type LocationSectionViewedTrackerProps = {
  pageType?: PageType;
};

const LocationSectionViewedTracker = ({ pageType }: LocationSectionViewedTrackerProps) => {
  const viewedRef = useRef<Record<string, boolean>>({});
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    if (!pageType) return;

    const elements = SECTIONS.map(({ id }) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const id = (entry.target as HTMLElement).id;
          const config = SECTIONS_BY_ID[id];
          if (!config) return;

          const requiredRatio = config.threshold ?? 0;
          const meetsThreshold = entry.intersectionRatio >= requiredRatio;

          if (!meetsThreshold || viewedRef.current[id]) {
            const t = timersRef.current[id];
            if (t) {
              clearTimeout(t);
            }
            delete timersRef.current[id];
            return;
          }
          if (timersRef.current[id]) return;
          timersRef.current[id] = setTimeout(() => {
            viewedRef.current[id] = true;
            logWebSectionViewed({ page_type: pageType, section_type: config.sectionType });
            delete timersRef.current[id];
          }, 1000);
        });
      },
      { threshold: THRESHOLDS }
    );

    elements.forEach(el => observer.observe(el));

    return () => {
      Object.values(timersRef.current).forEach(t => clearTimeout(t));
      timersRef.current = {};
      observer.disconnect();
    };
  }, [pageType]);

  return null;
};

export default LocationSectionViewedTracker;
